// src/pages/ProductsPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { ProductList } from '../components/ProductList';
import { Product } from '../types/Product'; // Import Product interface from new location
import { HARDCODED_CATEGORIES } from '../config/categories'; // Import hardcoded categories

interface ProductsPageProps {
  // products prop removed
}

const ProductsPage: React.FC<ProductsPageProps> = () => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const BACKEND_URL_ROOT = process.env.REACT_APP_BACKEND_URL_ROOT || 'http://localhost:3001';
  const API_PRODUCTS_URL = `${BACKEND_URL_ROOT}/api/products`;

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(API_PRODUCTS_URL); // Use API_PRODUCTS_URL
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Failed to fetch products and parse error response.' }));
          throw new Error(errorData.message || `Failed to fetch products. Status: ${response.status}`);
        }
        const data: Product[] = await response.json();
        setAllProducts(data);
      } catch (err: any) {
        console.error("Fetch products error:", err);
        setError(err.message || 'An unexpected error occurred while fetching products.');
        setAllProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [API_PRODUCTS_URL]); // Add API_PRODUCTS_URL here

  const categories = useMemo(() => {
    const dynamicCategories = allProducts.length > 0
      ? Array.from(new Set(allProducts.map(p => p.category).filter(Boolean)))
      : [];

    // Combine hardcoded and dynamic, ensuring uniqueness and sorting
    const combined = Array.from(new Set([...HARDCODED_CATEGORIES, ...dynamicCategories])).sort();

    return ['All Products', ...combined];
  }, [allProducts]);

  const filteredProducts = selectedCategory === 'All Products'
    ? allProducts
    : allProducts.filter(product => product.category === selectedCategory);

  return (
    <div className="products-page-container">
      <div className="category-panel">
        <h2 className="category-panel-title">Categories</h2>
        <ul className="category-list">
          {categories.map((category, index) => (
            <li
              key={index}
              onClick={() => setSelectedCategory(category)}
              className={`category-item ${selectedCategory === category ? 'active' : ''}`}
            >
              {category}
            </li>
          ))}
        </ul>
      </div>
      <div className="products-content">
        <h1 className="products-content-title">{selectedCategory}</h1>
        <ProductList products={filteredProducts} isLoading={isLoading} error={error} />
      </div>
    </div>
  );
};

export default ProductsPage;
